import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HistoryIcon from '@mui/icons-material/History';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from 'axios';
import moment from 'moment';

// --- Instruction Checker Logic ---
function analyzeInstructions(content) {
  const analysis = {
    score: 0,
    issues: [],
    suggestions: [],
    strengths: []
  };
  const text = (content || '').toLowerCase();
  const wordCount = (content || '').trim().split(/\s+/).length;
  const sentences = (content || '').split(/[.!?]+/).filter(s => s.trim().length > 0);

  // Length analysis
  if (wordCount < 10) {
    analysis.issues.push({
      type: 'error',
      message: 'Instructions are too short',
      detail: 'Add more detail about the agent\'s personality, behavior, and guidelines.'
    });
  } else if (wordCount < 30) {
    analysis.issues.push({
      type: 'warning',
      message: 'Instructions could be more detailed',
      detail: 'Consider adding more specific guidelines and examples.'
    });
  } else if (wordCount > 500) {
    analysis.issues.push({
      type: 'warning',
      message: 'Instructions are quite long',
      detail: 'Very long instructions might be harder for the AI to follow consistently.'
    });
  } else {
    analysis.strengths.push('Good instruction length');
  }

  // Personality and tone analysis
  const personalityKeywords = ['friendly', 'professional', 'casual', 'formal', 'helpful', 'polite', 'enthusiastic', 'calm', 'energetic'];
  const hasPersonality = personalityKeywords.some(keyword => text.includes(keyword));
  if (!hasPersonality) {
    analysis.suggestions.push({
      type: 'suggestion',
      message: 'Consider defining personality traits',
      detail: 'Add words like "friendly", "professional", or "helpful" to define the agent\'s tone.'
    });
  } else {
    analysis.strengths.push('Personality traits defined');
  }

  // Role definition
  const roleKeywords = ['you are', 'act as', 'your role', 'assistant', 'helper', 'expert', 'specialist'];
  const hasRole = roleKeywords.some(keyword => text.includes(keyword));
  if (!hasRole) {
    analysis.suggestions.push({
      type: 'suggestion',
      message: 'Define the agent\'s role clearly',
      detail: 'Start with "You are..." or "Act as..." to clearly define what the agent should be.'
    });
  } else {
    analysis.strengths.push('Clear role definition');
  }

  // Behavioral guidelines
  const behaviorKeywords = ['should', 'must', 'always', 'never', 'avoid', 'ensure', 'remember'];
  const hasBehavior = behaviorKeywords.some(keyword => text.includes(keyword));
  if (!hasBehavior) {
    analysis.suggestions.push({
      type: 'suggestion',
      message: 'Add behavioral guidelines',
      detail: 'Include specific dos and don\'ts for the agent\'s behavior.'
    });
  } else {
    analysis.strengths.push('Clear behavioral guidelines');
  }

  // Domain expertise
  const domainKeywords = ['expert in', 'specializes in', 'knowledge of', 'experience with', 'trained in'];
  const hasDomain = domainKeywords.some(keyword => text.includes(keyword));
  if (hasDomain) {
    analysis.strengths.push('Domain expertise specified');
  }

  // Conversation handling
  const conversationKeywords = ['respond', 'answer', 'question', 'conversation', 'chat', 'discuss'];
  const hasConversation = conversationKeywords.some(keyword => text.includes(keyword));
  if (!hasConversation) {
    analysis.suggestions.push({
      type: 'suggestion',
      message: 'Add conversation guidelines',
      detail: 'Include how the agent should handle questions and maintain conversations.'
    });
  }

  // Boundaries and limitations
  const boundaryKeywords = ['cannot', 'unable', "don't", "won't", 'limit', 'boundary', 'restriction'];
  const hasBoundaries = boundaryKeywords.some(keyword => text.includes(keyword));
  if (!hasBoundaries) {
    analysis.suggestions.push({
      type: 'suggestion',
      message: 'Consider adding limitations',
      detail: 'Specify what the agent cannot or should not do.'
    });
  } else {
    analysis.strengths.push('Clear boundaries defined');
  }

  // Clarity and structure
  if (sentences.length < 3) {
    analysis.issues.push({
      type: 'warning',
      message: 'Instructions lack structure',
      detail: 'Break instructions into multiple clear sentences or bullet points.'
    });
  }

  // Contradictions check
  const positiveWords = ['friendly', 'helpful', 'polite', 'professional'];
  const negativeWords = ['rude', 'harsh', 'dismissive', 'cold'];
  const hasPositive = positiveWords.some(word => text.includes(word));
  const hasNegative = negativeWords.some(word => text.includes(word));
  if (hasPositive && hasNegative) {
    analysis.issues.push({
      type: 'warning',
      message: 'Potentially contradictory instructions',
      detail: 'Review for conflicting personality traits or behaviors.'
    });
  }

  // Calculate score
  let score = 0;
  score += Math.min(wordCount / 100 * 30, 30); // Length score (max 30)
  score += analysis.strengths.length * 10; // Strengths (10 points each)
  score -= analysis.issues.filter(i => i.type === 'error').length * 15; // Errors (-15 each)
  score -= analysis.issues.filter(i => i.type === 'warning').length * 5; // Warnings (-5 each)
  score = Math.max(0, Math.min(100, score));
  analysis.score = Math.round(score);
  return analysis;
}

const bestPractices = [
  'Define a clear role and personality',
  'Include specific behavioral guidelines',
  'Set boundaries and limitations',
  'Use examples for complex scenarios',
  'Keep instructions concise but detailed',
  'Test with different conversation flows'
];

export const INSTRUCTION_TEMPLATES = {
  customerSupport: `You are \[Agent Name], an empathetic, knowledgeable, and solution-focused customer support specialist dedicated to delivering excellent customer experiences.\n\nBehaviors:\n\n- Always greet users warmly by name (if known).\n- Quickly identify and restate the customer's issue to confirm understanding.\n- Provide precise solutions from knowledge bases or FAQs.\n- Escalate unresolved, sensitive, or complex requests to a human agent promptly.\n- Politely close conversations by confirming customer satisfaction.\n\nAgent Tone:\n\n- Friendly, reassuring, empathetic.\n- Clear and concise language.\n- Positive and supportive attitude.\n\nObjectives:\n\n- Achieve customer satisfaction ratings of 90% or higher.\n- Efficiently resolve common queries to reduce human-agent workload.\n- Accurately route and escalate complex issues.\n\nRules:\n\n- Never share personal or sensitive customer information without verification.\n- Provide knowledge-base links when possible.\n- Escalate immediately if a customer requests a human or is frustrated after two interactions.\n- Use today's date for any time-specific answers.`,
  salesAssistant: `You are {Agent Name], an energetic, proactive, and persuasive sales specialist, passionate about clearly communicating the value of products or services to potential customers.\n\nBehaviors:\n\n- Greet users confidently and with enthusiasm.\n- Qualify leads by understanding their needs, industry, and purchasing timeline.\n- Clearly articulate the benefits and unique value of the product.\n- Recommend demos, trials, or direct users to sales resources as appropriate.\n- Capture lead details for follow-up actions.\n\nAgent Tone:\n\n- Confident, positive, and engaging.\n- Consultative and enthusiastic without being pushy.\n- Clear, direct, and conversational style.\n\nObjectives\n- Increase qualified lead generation.\n- Convert chat interactions into demo bookings or sales meetings.\n- Provide personalized experiences by leveraging customer data.\n\nRules\n- Never promise custom pricing or discounts without human approval.\n- Always obtain contact details (name, email, phone) for qualified leads.\n- Avoid overly aggressive selling tactics; focus on customer benefits.\n- Escalate highly-qualified or high-value leads immediately to sales team.`,
  technicalExpert: `You are \[Agent Name], a patient, methodical, and technically adept support engineer who efficiently diagnoses and troubleshoots technical issues, providing clear, step-by-step guidance.\n\nBehaviors:\n\n- Quickly gather relevant technical details (error codes, system versions, screenshots).\n- Diagnose issues systematically using predefined troubleshooting steps.\n- Clearly document and communicate steps for resolution.\n- Escalate urgent, complex, or unresolved issues promptly with detailed logs.\n- Confirm resolution before closing the interaction.\n\nAgent Tone:\n\n- Calm, patient, supportive.\n- Clear technical explanations without jargon.\n- Methodical, step-by-step instructional style.\n\nObjectives:\n\n- Reduce mean-time-to-resolution (MTTR) for technical issues.\n- Accurately diagnose and document technical issues.\n- Improve user satisfaction and reduce human agent intervention for common issues.\n\nRules:\n\n- Never execute or advise risky actions without human validation.\n- Always request user permission before accessing any system or account details.\n- Escalate immediately if the user expresses frustration or if critical infrastructure is affected.\n- Document each interaction clearly, tagging with relevant issue categories.
`
};

function arrayToDate(arr) {
  if (!Array.isArray(arr) || arr.length < 6) return null;
  return new Date(
    arr[0],
    arr[1] - 1,
    arr[2],
    arr[3],
    arr[4],
    arr[5],
    arr[6] ? Math.floor(arr[6] / 1000000) : 0
  );
}

function formatHistoryDate(value) {
  if (!value) return '-';
  if (Array.isArray(value)) {
    const date = arrayToDate(value);
    return date ? moment(date).format('MMM D, YYYY h:mm A') : '-';
  }
  return moment(value).format('MMM D, YYYY h:mm A');
}

export default function InstructionChecker({
  content,
  onTemplateInsert,
  onContentRestore,
  instructionId,
  width = '100%'
}) {
  const analysis = useMemo(() => analyzeInstructions(content), [content]);

  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };
  const getScoreText = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const [expandedPanel, setExpandedPanel] = useState('strengths');

  useEffect(() => {
    if (!instructionId) {
      setHistoryRows([]);
      setHistoryError(null);
      setHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);

    axios.post(`/instructions/history?id=${instructionId}`)
      .then(res => {
        if (!cancelled) {
          setHistoryRows(res.data || []);
          setHistoryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryError('Failed to load history.');
          setHistoryRows([]);
          setHistoryLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [instructionId]);

  const handleOpenHistoryDialog = (row, versionNumber) => {
    setSelectedHistory({ ...row, versionNumber });
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedHistory(null);
  };

  const handleUseHistoryContent = () => {
    if (selectedHistory?.content && onContentRestore) {
      onContentRestore(selectedHistory.content);
    }
    handleCloseHistoryDialog();
  };

  const renderHistoryContent = () => {
    if (!instructionId) {
      return (
        <Typography variant="body2" color="text.secondary">
          No history available.
        </Typography>
      );
    }
    if (historyLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (historyError) {
      return (
        <Typography variant="body2" color="error">
          {historyError}
        </Typography>
      );
    }
    if (historyRows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No history available.
        </Typography>
      );
    }

    const historyColSx = {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center'
    };

    return (
      <Box sx={{ maxHeight: 152, overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 0,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}
        >
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            Version
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            Date
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={historyColSx}>
            User
          </Typography>
          <Box sx={{ ...historyColSx, justifyContent: 'flex-end' }} />
        </Box>
        <List dense disablePadding>
          {historyRows.map((row, index) => {
            const versionNumber = historyRows.length - index;
            const formattedDate = formatHistoryDate(row.historyDateCreated);
            const userName = row.updatedBy || '-';
            return (
            <ListItem
              key={row.historyId}
              sx={{
                px: 0,
                py: 0.5,
                minHeight: 40,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="caption" sx={historyColSx}>
                #{versionNumber}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...historyColSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={formattedDate}
              >
                {formattedDate}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...historyColSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={userName}
              >
                {userName}
              </Typography>
              <Box sx={{ ...historyColSx, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<NavigateNextIcon />}
                  onClick={() => handleOpenHistoryDialog(row, versionNumber)}
                  sx={{ flexShrink: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                >
                  View
                </Button>
              </Box>
            </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ width }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LightbulbIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">Instruction Checker</Typography>
      </Box>
      {content && content.trim() ? (
        <>
          {/* Score Display */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Quality Score</Typography>
              <Typography variant="body2" fontWeight="bold">
                {analysis.score}/100
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={analysis.score}
              color={getScoreColor(analysis.score)}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography
              variant="caption"
              color={`${getScoreColor(analysis.score)}.main`}
              fontWeight="bold"
            >
              {getScoreText(analysis.score)}
            </Typography>
          </Box>
          {/* Strengths */}
          {analysis.strengths.length > 0 && (
            <Accordion expanded={expandedPanel === 'strengths'} onChange={() => setExpandedPanel(expandedPanel === 'strengths' ? false : 'strengths')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle2">
                    Strengths ({analysis.strengths.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense>
                  {analysis.strengths.map((strength, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={strength}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {/* Issues */}
          {analysis.issues.length > 0 && (
            <Accordion expanded={expandedPanel === 'issues'} onChange={() => setExpandedPanel(expandedPanel === 'issues' ? false : 'issues')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {analysis.issues.some(i => i.type === 'error') ? (
                    <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                  ) : (
                    <WarningIcon color="warning" sx={{ mr: 1, fontSize: 20 }} />
                  )}
                  <Typography variant="subtitle2">
                    Issues ({analysis.issues.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense>
                  {analysis.issues.map((issue, index) => (
                    <ListItem key={index} sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          {issue.type === 'error' ? (
                            <ErrorIcon color="error" sx={{ fontSize: 16 }} />
                          ) : (
                            <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                          )}
                        </ListItemIcon>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {issue.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {issue.detail}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <Accordion expanded={expandedPanel === 'suggestions'} onChange={() => setExpandedPanel(expandedPanel === 'suggestions' ? false : 'suggestions')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle2">
                    Suggestions ({analysis.suggestions.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense>
                  {analysis.suggestions.map((suggestion, index) => (
                    <ListItem key={index} sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          <InfoIcon color="info" sx={{ fontSize: 16 }} />
                        </ListItemIcon>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {suggestion.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {suggestion.detail}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {/* Best Practices */}
          <Accordion sx={{ mt: 2 }} expanded={expandedPanel === 'bestPractices'} onChange={() => setExpandedPanel(expandedPanel === 'bestPractices' ? false : 'bestPractices')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LightbulbIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="subtitle2">Best Practices</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <List dense>
                {bestPractices.map((tip, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LightbulbIcon color="primary" sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={tip}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
          {/* Example Templates */}
          <Accordion sx={{ mt: 1 }} expanded={expandedPanel === 'templates'} onChange={() => setExpandedPanel(expandedPanel === 'templates' ? false : 'templates')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="subtitle2">Example Templates</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Customer Support Agent:
                  </Typography>
                  {onTemplateInsert && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onTemplateInsert(INSTRUCTION_TEMPLATES.customerSupport)}
                      sx={{ fontSize: '0.6rem', minWidth: 'auto', p: 0.5 }}
                    >
                      Use Template
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                  "You are a friendly and professional customer support agent. Always be helpful, patient, and empathetic..."
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Sales Assistant:
                  </Typography>
                  {onTemplateInsert && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onTemplateInsert(INSTRUCTION_TEMPLATES.salesAssistant)}
                      sx={{ fontSize: '0.6rem', minWidth: 'auto', p: 0.5 }}
                    >
                      Use Template
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                  "You are an enthusiastic sales assistant specializing in our products. Be knowledgeable about features..."
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Technical Expert:
                  </Typography>
                  {onTemplateInsert && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onTemplateInsert(INSTRUCTION_TEMPLATES.technicalExpert)}
                      sx={{ fontSize: '0.6rem', minWidth: 'auto', p: 0.5 }}
                    >
                      Use Template
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                  "You are a technical expert with deep knowledge in software development. Provide accurate, detailed explanations..."
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 1 }}>
          Start typing instructions to see real-time analysis and suggestions.
        </Alert>
      )}

      {/* History - always visible */}
      <Accordion
        sx={{ mt: 2 }}
        expanded={expandedPanel === 'history'}
        onChange={() => setExpandedPanel(expandedPanel === 'history' ? false : 'history')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle2">
              History{historyRows.length > 0 ? ` (${historyRows.length})` : ''}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {renderHistoryContent()}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {selectedHistory ? `Version ${selectedHistory.versionNumber}` : 'History'}
          {selectedHistory && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {formatHistoryDate(selectedHistory.historyDateCreated)}
              {selectedHistory.updatedBy ? ` · ${selectedHistory.updatedBy}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', m: 0 }}
            >
              {selectedHistory?.content || ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
          {onContentRestore && (
            <Button variant="contained" onClick={handleUseHistoryContent}>
              Use this version
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
